import { useEffect, useRef } from 'react'
import { useDream } from '@/hooks/useDream'
import { useUserContext } from '@/store/contexts/UserContext'
import { pushDreamitySnapshot, syncDreamitySnapshotPullThenMaybePush } from '@/services/dreamitySyncService'
import apiClient from '@/services/api/client'
import { SERVICE_NAME, API_BASE_URL } from '@/config/api'
import { isNative as isReactNativeWebView } from '@/utils/platform'

/**
 * visibilitychange:
 * - hidden: 서버로 스냅샷 push (dirty일 때만 pushDreamitySnapshot 내부 처리)
 * - visible: pull 후 필요 시 push (토리투두와 동일한 백그라운드/포그라운드 동기화)
 * 
 * 앱 사용량 동기화:
 * - BACKGROUND 전환 시(hidden): 사용량 기록 API 호출
 * - native-resume: 사용량 시간 초기화
 */
function AppVisibilityRefresh() {
  const { fetchCurrentUser, isAuthenticated } = useUserContext()
  const { fetchDreams } = useDream()
  const appStartTimeRef = useRef<number>(Date.now())
  const wasHiddenRef = useRef<boolean>(false)

  useEffect(() => {
    const runSilentRefresh = () => {
      if (!isAuthenticated) return
      fetchCurrentUser({ silent: true })
        .then(() => syncDreamitySnapshotPullThenMaybePush())
        .then(() => {
          void fetchDreams()
        })
        .catch(() => {})
    }

    const runSyncPush = () => {
      if (!isAuthenticated) return
      void pushDreamitySnapshot()
    }

    const recordAppUsage = (startTime?: number, endTime?: number, usageTimeMs?: number, isClosing = false) => {
      if (!isAuthenticated) return
      const now = Date.now()
      const start = startTime || appStartTimeRef.current
      const end = endTime || now
      const usage = usageTimeMs || (end - start)

      const payload = {
        serviceName: SERVICE_NAME,
        state: 'BACKGROUND',
        startTime: start,
        endTime: end,
        usageTimeMs: usage
      }

      if (isClosing) {
        // 페이지가 닫힐 때(Refresh/Close)는 fetch + keepalive를 사용하여 요청이 취소되지 않도록 함
        const token = localStorage.getItem('token')
        const url = `${API_BASE_URL}/common/app-usage`
        void fetch(url, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'X-Service-Name': SERVICE_NAME,
            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
          },
          body: JSON.stringify(payload),
          keepalive: true
        }).catch(() => {}) // 종료 시 에러는 무시
      } else {
        apiClient.put('/common/app-usage', payload)
          .catch(err => console.error('[AppVisibilityRefresh] Usage status update failed', err))
      }
    }

    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') {
        wasHiddenRef.current = true
        runSyncPush()

        // sync push와 동일하게 WebView에서도 백그라운드 시 전송 (기존 RN 제외 시 app-usage 미호출)
        if (isAuthenticated) {
          recordAppUsage()
        }
        return
      }
      
      if (document.visibilityState !== 'visible' || !wasHiddenRef.current) return
      wasHiddenRef.current = false
      appStartTimeRef.current = Date.now() // 다시 포그라운드로 복귀 시 시간 초기화

      const root = document.getElementById('root')
      if (root && root.children.length === 0) {
        window.location.reload()
        return
      }

      runSilentRefresh()
    }

    const handleBeforeUnload = () => {
      // 새로고침이나 브라우저 종료 시에도 웹 환경이면 사용량 기록
      if (!isReactNativeWebView() && isAuthenticated) {
        recordAppUsage(undefined, undefined, undefined, true)
      }
    }

    const handleNativeResume = (event: Event) => {
      const nativeResumeEvent = event as CustomEvent<{ policy?: 'keep-state' | 'refresh-state' }>
      const policy = nativeResumeEvent.detail?.policy

      appStartTimeRef.current = Date.now()

      if (policy === 'keep-state') {
        runSilentRefresh()
        return
      }
      if (policy === 'refresh-state') {
        window.location.reload()
        return
      }

      runSilentRefresh()
    }

    const handleAppUsageStatus = (e: Event) => {
      const detail = (e as CustomEvent).detail
      if (detail && detail.state === 'BACKGROUND' && isAuthenticated) {
        recordAppUsage(detail.startTime, detail.endTime, detail.usageTimeMs)
      }
    }

    const handleNativeSyncPush = () => {
      runSyncPush()
    }

    // 초기 기동 시 Pull ─────
    if (isAuthenticated) {
      runSilentRefresh()
    }



    document.addEventListener('visibilitychange', handleVisibility)
    window.addEventListener('beforeunload', handleBeforeUnload)
    window.addEventListener('dreamity:native-resume', handleNativeResume as EventListener)
    window.addEventListener('dreamity:app-usage-status', handleAppUsageStatus as EventListener)
    window.addEventListener('dreamity:sync-push', handleNativeSyncPush as EventListener)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility)
      window.removeEventListener('beforeunload', handleBeforeUnload)
      window.removeEventListener('dreamity:native-resume', handleNativeResume as EventListener)
      window.removeEventListener('dreamity:app-usage-status', handleAppUsageStatus as EventListener)
      window.removeEventListener('dreamity:sync-push', handleNativeSyncPush as EventListener)
    }
  }, [fetchCurrentUser, fetchDreams, isAuthenticated])

  return null
}

export default AppVisibilityRefresh
